import dayjs from "dayjs";
import type * as GeoJSON from "geojson";

import { FrozenSimWeather, WeatherHour } from "../../types/storyMap";
import { WeatherGrid, WindField } from "../../types/weatherGrid";
import { toDisplaySpeed, toDisplayTemperature, UnitSystem } from "../../utils/units";
import { windFieldFromWeatherGrid } from "../../utils/weatherGrid";
import { SymbolLayout, SymbolPaint } from "./storyLayers";

/**
 * The story "weather" layer: a chosen simulation's weather at one hour —
 * animated wind particles (WindParticleLayer, DOM canvas over the map) plus
 * a symbol layer of wind arrows and temperature / humidity labels on the
 * weather lattice (or a coarse synthetic lattice for uniform weather).
 * Pure helpers over the sim's frozen/live weather; no fetching here.
 */

/** Normalised weather inputs of one simulation. */
export type SimWeatherData = FrozenSimWeather;

const WEATHER_KEYS: (keyof WeatherHour)[] = [
  "wind_speed",
  "wind_direction",
  "temperature",
  "relative_humidity",
];

function num(v: unknown): number | null {
  const n = typeof v === "string" ? Number(v) : (v as number);
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

/**
 * From a raw fireSimulationResult entry (`start_date_time`, `weather`,
 * `weather_grid`) or an already-frozen FrozenSimWeather. The live `weather`
 * dict may wrap its hours in `interval_weather` (older runs).
 */
export function simWeatherFromRaw(raw: {
  start_date_time?: string | null;
  weather?: unknown;
  weather_grid?: unknown;
}): SimWeatherData | null {
  let hours = raw.weather as Record<string, unknown> | null | undefined;
  if (hours && typeof hours === "object" && "interval_weather" in hours) {
    hours = (hours as { interval_weather?: Record<string, unknown> }).interval_weather;
  }
  const series: Record<string, WeatherHour> = {};
  if (hours && typeof hours === "object") {
    Object.entries(hours).forEach(([ts, values]) => {
      if (!values || typeof values !== "object") return;
      const v = values as Record<string, unknown>;
      const hour: WeatherHour = {};
      WEATHER_KEYS.forEach((k) => {
        const n = num(v[k]);
        if (n != null) hour[k] = n;
      });
      series[ts] = hour;
    });
  }
  const grid = raw.weather_grid as WeatherGrid | null | undefined;
  const validGrid =
    grid &&
    Array.isArray(grid.lats) &&
    Array.isArray(grid.lons) &&
    Array.isArray(grid.timestamps) &&
    grid.vars
      ? grid
      : null;
  if (Object.keys(series).length === 0 && !validGrid) return null;
  return { startDateTime: raw.start_date_time ?? null, series, grid: validGrid };
}

/** Hours the layer can show, chronological. */
export function weatherTimestamps(data: SimWeatherData | null | undefined): string[] {
  if (!data) return [];
  const keys = Object.keys(data.series);
  const all = keys.length ? keys : data.grid?.timestamps ?? [];
  return [...all].sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
}

/**
 * Which hour to show: the playback clock (start + elapsed, matched on the
 * hour, else the latest hour not after it) when animating, else the entry's
 * chosen hour, else the first.
 */
export function resolveWeatherTimestamp(
  data: SimWeatherData | null | undefined,
  chosen: string | null | undefined,
  elapsedSeconds: number | null | undefined
): string | null {
  const stamps = weatherTimestamps(data);
  if (!stamps.length) return null;
  if (elapsedSeconds != null && data?.startDateTime) {
    const target = dayjs(data.startDateTime).add(elapsedSeconds, "second");
    const exact = stamps.find((ts) => dayjs(ts).isSame(target, "hour"));
    if (exact) return exact;
    let best: string | null = null;
    stamps.forEach((ts) => {
      if (!dayjs(ts).isAfter(target)) best = ts;
    });
    return best ?? stamps[0];
  }
  if (chosen && stamps.includes(chosen)) return chosen;
  return stamps[0];
}

export interface WeatherPoint {
  lng: number;
  lat: number;
  temperature: number | null;
  humidity: number | null;
  windSpeed: number | null;
  windDirection: number | null;
}

export interface WeatherSample {
  timestamp: string;
  /** Uniform / centre-point wind for the hour. */
  windSpeed: number;
  windDirection: number;
  /** Per-point wind for the particle layer (null = uniform). */
  field: WindField | null;
  /** Label positions with their values. */
  points: WeatherPoint[];
}

export type Bbox = { top: number; bottom: number; left: number; right: number };

/** 3×3 label positions inset from the bbox (uniform weather has no lattice). */
function syntheticLattice(bbox: Bbox): { lng: number; lat: number }[] {
  const out: { lng: number; lat: number }[] = [];
  const w = bbox.right - bbox.left;
  const h = bbox.top - bbox.bottom;
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      out.push({
        lng: bbox.left + w * (0.2 + 0.3 * j),
        lat: bbox.bottom + h * (0.2 + 0.3 * i),
      });
    }
  }
  return out;
}

/** The layer's data for one hour; null when the sim has no weather then. */
export function sampleWeather(
  data: SimWeatherData | null | undefined,
  timestamp: string | null,
  bbox: Bbox | null | undefined
): WeatherSample | null {
  if (!data || !timestamp) return null;
  const hour = data.series[timestamp] ?? {};
  const grid = data.grid;
  const t = grid ? grid.timestamps.findIndex((ts) => ts === timestamp || dayjs(ts).isSame(timestamp, "hour")) : -1;
  const centre = (name: keyof WeatherHour): number | null => {
    const direct = hour[name];
    if (direct != null) return direct;
    if (grid && t >= 0) {
      const v = grid.vars?.[name]?.[t]?.[grid.centre_index];
      return typeof v === "number" ? v : null;
    }
    return null;
  };
  const windSpeed = centre("wind_speed") ?? 0;
  const windDirection = centre("wind_direction") ?? 0;

  let points: WeatherPoint[] = [];
  let field: WindField | null = null;
  if (grid && t >= 0) {
    field = windFieldFromWeatherGrid(grid, t);
    const cols = grid.lons.length;
    const at = (name: keyof WeatherHour, k: number): number | null => {
      const v = grid.vars?.[name]?.[t]?.[k];
      return typeof v === "number" && Number.isFinite(v) ? v : null;
    };
    grid.lats.forEach((lat, i) => {
      grid.lons.forEach((lng, j) => {
        const k = i * cols + j;
        points.push({
          lng,
          lat,
          temperature: at("temperature", k),
          humidity: at("relative_humidity", k),
          windSpeed: at("wind_speed", k),
          windDirection: at("wind_direction", k),
        });
      });
    });
  } else if (bbox) {
    points = syntheticLattice(bbox).map((p) => ({
      ...p,
      temperature: centre("temperature"),
      humidity: centre("relative_humidity"),
      windSpeed,
      windDirection,
    }));
  }
  return { timestamp, windSpeed, windDirection, field, points };
}

/** Label text for one point ("31°C · 18% · 35 km/h"). */
export function weatherLabelText(
  point: WeatherPoint,
  unitSystem: UnitSystem = "metric"
): string {
  const parts: string[] = [];
  if (point.temperature != null) {
    const shown = Math.round(toDisplayTemperature(point.temperature, unitSystem));
    parts.push(`${shown}°${unitSystem === "imperial" ? "F" : "C"}`);
  }
  if (point.humidity != null) parts.push(`${Math.round(point.humidity)}%`);
  if (point.windSpeed != null) {
    const shown = Math.round(toDisplaySpeed(point.windSpeed, unitSystem));
    parts.push(`${shown} ${unitSystem === "imperial" ? "mph" : "km/h"}`);
  }
  return parts.join(" · ");
}

/**
 * Symbol-layer features: one per lattice point, arrow rotated to where the
 * wind blows TO (met. direction is where it comes FROM; the SDF arrow icon
 * points +x, i.e. east, so subtract 90).
 */
export function weatherLabelGeojson(
  sample: WeatherSample | null,
  unitSystem: UnitSystem = "metric"
): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: (sample?.points ?? []).map((p) => ({
      type: "Feature",
      properties: {
        label: weatherLabelText(p, unitSystem),
        rotate: p.windDirection != null ? (p.windDirection + 180 - 90 + 360) % 360 : 0,
        hasWind: p.windDirection != null,
      },
      geometry: { type: "Point", coordinates: [p.lng, p.lat] },
    })),
  };
}

export const WEATHER_ARROW_IMAGE_ID = "story-weather-arrow-icon";

// Map symbols are arrows only — the readings live in the side panel
// (WeatherPanelCard), where they are legible.
export const WEATHER_LABEL_LAYOUT: SymbolLayout = {
  "icon-image": WEATHER_ARROW_IMAGE_ID,
  "icon-size": 1.8,
  "icon-rotate": ["get", "rotate"],
  "icon-rotation-alignment": "map",
  "icon-allow-overlap": true,
  "icon-ignore-placement": true,
};

export const WEATHER_LABEL_PAINT: SymbolPaint = {
  "icon-color": "#ffffff",
  "icon-halo-color": "#212b37",
  "icon-halo-width": 1.5,
  "icon-opacity": ["case", ["get", "hasWind"], 0.95, 0],
};

/** Compass sector the wind blows FROM ("NW"). */
export function compassLabel(direction: number | null | undefined): string {
  if (direction == null || !Number.isFinite(direction)) return "";
  const sectors = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return sectors[Math.round((((direction % 360) + 360) % 360) / 45) % 8];
}

/** One reading with its across-the-map range (grids only). */
export interface WeatherReading {
  value: number | null;
  range: [number, number] | null;
}

/** Everything the readings panel shows for one hour. */
export interface WeatherPanelData {
  timestamp: string;
  temperature: WeatherReading;
  humidity: WeatherReading;
  windSpeed: WeatherReading;
  /** Met. direction the wind comes from (centre / uniform). */
  windDirection: number | null;
  /** True when the run had a spatial grid (ranges are meaningful). */
  isGrid: boolean;
  pointCount: number;
}

function reading(
  value: number | null,
  values: (number | null)[],
  isGrid: boolean
): WeatherReading {
  const nums = values.filter((v): v is number => v != null && Number.isFinite(v));
  const range: [number, number] | null =
    isGrid && nums.length > 1 ? [Math.min(...nums), Math.max(...nums)] : null;
  return { value, range };
}

/** Panel readings for a sample: centre values + min–max across the lattice. */
export function weatherPanelFrom(
  sample: WeatherSample | null,
  data: SimWeatherData | null | undefined
): WeatherPanelData | null {
  if (!sample) return null;
  const isGrid = !!sample.field;
  const hour = data?.series[sample.timestamp] ?? {};
  const centreTemp = hour.temperature ?? sample.points[0]?.temperature ?? null;
  const centreRh = hour.relative_humidity ?? sample.points[0]?.humidity ?? null;
  return {
    timestamp: sample.timestamp,
    temperature: reading(centreTemp, sample.points.map((p) => p.temperature), isGrid),
    humidity: reading(centreRh, sample.points.map((p) => p.humidity), isGrid),
    windSpeed: reading(sample.windSpeed, sample.points.map((p) => p.windSpeed), isGrid),
    windDirection: sample.windDirection,
    isGrid,
    pointCount: sample.points.length,
  };
}

/** Display strings for a reading ("34°C", "28–36°C"). */
export function formatReading(
  reading: WeatherReading,
  kind: "temperature" | "humidity" | "wind",
  unitSystem: UnitSystem = "metric"
): { value: string; range: string | null; unit: string } {
  const convert = (v: number) =>
    kind === "temperature"
      ? Math.round(toDisplayTemperature(v, unitSystem))
      : kind === "wind"
      ? Math.round(toDisplaySpeed(v, unitSystem))
      : Math.round(v);
  const unit =
    kind === "temperature"
      ? unitSystem === "imperial" ? "°F" : "°C"
      : kind === "wind"
      ? unitSystem === "imperial" ? "mph" : "km/h"
      : "%";
  return {
    value: reading.value != null ? String(convert(reading.value)) : "–",
    range: reading.range ? `${convert(reading.range[0])}–${convert(reading.range[1])}` : null,
    unit,
  };
}

/** One-line summary for print ("34°C · 12% RH · wind 35 km/h from W"). */
export function weatherSummaryText(
  panel: WeatherPanelData,
  unitSystem: UnitSystem = "metric"
): string {
  const t = formatReading(panel.temperature, "temperature", unitSystem);
  const h = formatReading(panel.humidity, "humidity", unitSystem);
  const w = formatReading(panel.windSpeed, "wind", unitSystem);
  const withRange = (r: ReturnType<typeof formatReading>) =>
    r.range ? `${r.value}${r.unit} (${r.range}${r.unit})` : `${r.value}${r.unit}`;
  const from = compassLabel(panel.windDirection);
  return `${withRange(t)} · ${withRange(h)} RH · wind ${withRange(w)}${from ? ` from ${from}` : ""}`;
}

/** Short hour label for chips / legend ("Tue 14 Jan 15:00"). */
export function formatWeatherTimestamp(ts: string | null | undefined): string {
  if (!ts) return "";
  const d = dayjs(ts);
  return d.isValid() ? d.format("ddd D MMM HH:mm") : ts;
}
